import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import type { DiaryViewT, StatefulDiaryT } from "@patient-watch/types";
import { diaryKeys } from "./diaryKeys";

export function useDiaryInvalidation() {
    const queryClient = useQueryClient();

    return {
        invalidateAll: () => {
            queryClient.invalidateQueries({ queryKey: diaryKeys.all });
        },
        invalidateById: (diaryId: string) => {
            queryClient.invalidateQueries({ queryKey: diaryKeys.detail(diaryId) });
        },
        invalidateByPatient: (patientId: string) => {
            queryClient.invalidateQueries({
                queryKey: diaryKeys.byPatient(patientId),
            });
        },
        invalidateBySettings: (filters: Record<string, unknown> = {}) => {
            queryClient.invalidateQueries({
                queryKey: diaryKeys.bySettings(filters),
            });
        },
        invalidateTabs: (filters: Record<string, unknown> = {}) => {
            queryClient.invalidateQueries({ queryKey: diaryKeys.tabs(filters) });
        },
    };
}

/**
 * Syncs the diary cache when a single diary's tab or status differs from cached list data.
 * This handles the case where the API computes fresh tab/status values that differ from
 * the persisted values in diaries_view.
 *
 * @param queryClient - The React Query client
 * @param diary - The freshly fetched diary with computed tab/status
 * @returns true if stale data was found and invalidated
 */
/** Keep the patient diary list cache aligned with a freshly loaded diary detail (nav badge, etc.). */
export function patchPatientDiariesCache(queryClient: QueryClient, diary: StatefulDiaryT, patientId: string): void {
    queryClient.setQueryData<StatefulDiaryT[]>(diaryKeys.byPatient(patientId), (existing) => {
        if (!existing?.length) {
            return [diary];
        }

        const index = existing.findIndex((d) => d.diary_id === diary.diary_id);
        if (index === -1) {
            return [...existing, diary];
        }

        const next = [...existing];
        next[index] = diary;
        return next;
    });
}

export async function syncDiaryCacheIfStale(queryClient: QueryClient, diary: StatefulDiaryT): Promise<boolean> {
    // Check all cached diaries queries for this diary
    const cachedQueries = queryClient.getQueriesData<DiaryViewT[]>({
        queryKey: diaryKeys.all,
    });

    let foundStaleData = false;
    const tabsToInvalidate = new Set<string>();

    for (const [, cachedData] of cachedQueries) {
        if (Array.isArray(cachedData)) {
            const cachedDiary = cachedData.find((d) => d.diary_id === diary.diary_id);
            if (cachedDiary) {
                // Check if tab or diary_status has changed
                if (cachedDiary.tab !== diary.tab || cachedDiary.diary_status !== diary.diary_status) {
                    foundStaleData = true;
                    // Track which tabs need invalidation (both old and new)
                    if (cachedDiary.tab) tabsToInvalidate.add(cachedDiary.tab);
                    if (diary.tab) tabsToInvalidate.add(diary.tab);
                }
            }
        }
    }

    if (foundStaleData) {
        console.log(
            `[syncDiaryCacheIfStale] Diary ${diary.diary_id} has stale tab/status. Invalidating tabs:`,
            Array.from(tabsToInvalidate)
        );

        // Invalidate queries for affected tabs
        for (const tab of tabsToInvalidate) {
            // Invalidate bySettings queries that match this tab
            queryClient.invalidateQueries({
                queryKey: diaryKeys.all,
                predicate: (query) => {
                    const key = query.queryKey;
                    // Check if this is a bySettings query with matching tab
                    if (Array.isArray(key) && key[1] === "bySettings") {
                        const settings = key[2] as Record<string, unknown> | undefined;
                        return settings?.tab === tab;
                    }
                    // Also invalidate count queries for this tab
                    if (Array.isArray(key) && key[1] === "count") {
                        const settings = key[2] as Record<string, unknown> | undefined;
                        return settings?.tab === tab;
                    }
                    return false;
                },
            });
        }

        // Refetch active queries in background
        queryClient.refetchQueries({
            queryKey: diaryKeys.all,
            type: "active",
            predicate: (query) => {
                const key = query.queryKey;
                // Only refetch bySettings and count queries
                if (Array.isArray(key)) {
                    return key[1] === "bySettings" || key[1] === "count";
                }
                return false;
            },
        });
    }

    return foundStaleData;
}
